import { useLayoutEffect, useRef, useState } from 'react';
import { explicarFila } from './ganttExplica';

/**
 * La tarjeta que aparece al pasar por una barra.
 *
 * Reemplaza al `title` nativo, que mostraba tres lineas sin ningun porque y ademas tarda un
 * segundo largo en aparecer. Lo que importa de una barra de este Gantt no es que dice, es
 * **de donde salio**: la mayoria de las fechas las dedujo el backend.
 *
 * Es una sola tarjeta para todo el grafico —no una por fila—: con 2000 filas, montar un
 * componente flotante por barra es lo que vuelve lento un Gantt.
 */
export default function GanttBarraCard({ row, rect, horasPorDia }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!row || !rect || !ref.current) { setPos(null); return; }
    const caja = ref.current.getBoundingClientRect();
    const margen = 8;
    let left = rect.left;
    let top = rect.bottom + margen;
    if (left + caja.width > window.innerWidth - margen) left = window.innerWidth - caja.width - margen;
    if (left < margen) left = margen;
    if (top + caja.height > window.innerHeight - margen) top = rect.top - caja.height - margen;
    setPos({ left, top });
  }, [row, rect]);

  if (!row) return null;
  // La jornada de la capsula, que el backend manda en `meta.horas_por_dia`. Sin esto el tooltip
  // dividia por 8 fijo y contradecia al servidor en una capsula de jornada distinta.
  const x = explicarFila(row, { horasPorDia });
  if (!x) return null;

  return (
    <div
      ref={ref}
      data-testid="gantt-barra-card"
      className="fixed z-40 w-72 bg-white border border-brand-border rounded-sm shadow-lg pointer-events-none"
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
    >
      <div className="px-3 py-2 border-b border-brand-border bg-zinc-50">
        <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-zinc-500">
          {x.tipoLabel}{row.wbs ? ` · ${row.wbs}` : ''}
        </div>
        <div className="text-xs font-semibold text-[#09090B] leading-tight mt-0.5">{row.label}</div>
        <div className="text-[10px] text-zinc-500 mt-1 leading-snug">{x.tipoNota}</div>
      </div>

      <div className="px-3 py-2 space-y-1">
        {x.campos.map((c) => (
          <div key={c.k} className="flex items-baseline gap-2 text-[11px]">
            <span className="text-zinc-500 w-16 shrink-0">{c.k}</span>
            <span className={`font-mono ${c.flojo ? 'text-zinc-500' : 'text-[#09090B]'}`}>{c.v}</span>
            {c.nota && <span className="text-[9px] text-zinc-400 truncate">{c.nota}</span>}
          </div>
        ))}
        {/* **Dos renglones y no uno.** Antes habia un solo «Avance» que era el consumo, asi que
            una tarea que insumio el doble decia 100 % sin estar hecha. Un guion cuando nadie lo
            declaro: un cero afirmaria que no se avanzo. */}
        <div className="flex items-baseline gap-2 text-[11px]">
          <span className="text-zinc-500 w-16 shrink-0">Avance</span>
          <span className="font-mono text-[#09090B]">
            {x.progreso.pct == null ? '—' : `${x.progreso.pct}%`}
          </span>
          <span className="text-[9px] text-zinc-400 truncate">{x.progreso.nota}</span>
        </div>
        {x.progreso.consumoPct != null && (
          <div className="flex items-baseline gap-2 text-[11px]">
            <span className="text-zinc-500 w-16 shrink-0">Gastado</span>
            <span className={`font-mono ${x.progreso.seFueDeMano ? 'text-brand-danger' : 'text-[#09090B]'}`}>
              {x.progreso.consumoPct}%
            </span>
            <span className="text-[9px] text-zinc-400 truncate">{x.progreso.consumoNota}</span>
          </div>
        )}
      </div>

      {x.avisos.length > 0 && (
        <div className="px-3 py-2 border-t border-brand-border bg-[#FFFCF2] space-y-1">
          {x.avisos.map((a) => (
            <p key={a} className="text-[10px] text-[#8A6400] leading-snug">{a}</p>
          ))}
        </div>
      )}
    </div>
  );
}
